import React, { useRef, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { SPEDocument } from '../../services/sharePointEmbeddedService';

interface SimpleContextMenuProps {
  document: SPEDocument;
  position: { x: number; y: number };
  isVisible: boolean;
  onClose: () => void;
  onAction: (action: string, document: SPEDocument) => void;
}

export const SimpleContextMenu: React.FC<SimpleContextMenuProps> = ({
  document: speDocument,
  position,
  isVisible,
  onClose,
  onAction
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Close menu on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, onClose]);

  if (!isVisible) {
    return null;
  }

  const handleDelete = () => {
    onAction('delete', speDocument);
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 py-1 min-w-[150px]"
      style={{
        left: position.x,
        top: position.y
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Document name header */}
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {speDocument.name}
        </div>
      </div>

      {/* Delete option */}
      <button
        onClick={handleDelete}
        className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
      >
        <Trash2 className="h-4 w-4" />
        <span>Delete</span>
      </button>
    </div>
  );
};

export default SimpleContextMenu;
